import firestoreUser, {
  CreatedOrUpdatedOrDeletedBy,
  Permissions as OldPermission,
  UserSource,
  UserSourceChannel,
} from '../user'

import firebase from 'firebase/compat/app'
import camelCase from "lodash-es/camelCase"
import cloneDeep from "lodash-es/cloneDeep"
import moment from 'moment'
import UsersService from '../../services/UsersService'
import BaseModel from './base'

export interface DialMobileApp { [key: string]: boolean }
export interface DialWebApp { [key: string]: boolean }

interface Permissions {
  campaignsEnabled?: boolean
  campaignsReadOnly?: boolean
  contactsEnabled?: boolean
  workflowsEnabled?: boolean
  workflowsReadOnly?: boolean
  triggersEnabled?: boolean
  funnelsEnabled?: boolean
  websitesEnabled?: boolean
  opportunitiesEnabled?: boolean
  dashboardStatsEnabled?: boolean
  bulkRequestsEnabled?: boolean
  appointmentsEnabled?: boolean
  reviewsEnabled?: boolean
  onlineListingsEnabled?: boolean
  phoneCallEnabled?: boolean
  conversationsEnabled?: boolean
  assignedDataOnly?: boolean
  adwordsReportingEnabled?: boolean
  membershipEnabled?: boolean
  facebookAdsReportingEnabled?: boolean
  attributionsReportingEnabled?: boolean
  agentReportingEnabled?: boolean
  botService?: boolean
  settingsEnabled?: boolean
  tagsEnabled?: boolean
  leadValueEnabled?: boolean
  marketingEnabled?: boolean
  bloggingEnabled?: boolean
  invoiceEnabled?: boolean
  contentAiEnabled?: boolean
  communitiesEnabled?: boolean
  certificatesEnabled?: boolean
  mediaStorageEnabled?: boolean
}

export interface GmailTwoWaySync {
  integrationId?: string
  email?: string
}

export interface OutlookTwoWaySync {
  integrationId?: string
  email?: string
}

export interface TwoWaySync {
  gmail?: GmailTwoWaySync
  outlook?: OutlookTwoWaySync
}

export interface IUser {
  id?: string
  firstName?: string
  lastName?: string
  email?: string
  password?: string
  phone?: string
  extension?: string
  companyId?: string
  lastLoginTime?: string
  twilioPhone?: { [key: string]: string }
  userCalendar?: { [key: string]: string }
  role?: string
  type?: string
  locationWiseTimezone?: { [key: string]: string }
  locationWiseMeetingLocation?: { [key: string]: string }
  locationWiseZoomAdded?: { [key: string]: string }
  isPasswordPending?: boolean
  locationWiseOpenHours?: { [key: string]: { [key: string]: any }[] }
  locations?: { [key: string]: string }
  profilePhoto?: string
  emailProviderId?: string
  emailSignature?: string
  calendar?: string
  postedForSaas?: boolean
  permissions?: Permissions
  createdBy?: CreatedOrUpdatedOrDeletedBy
  lastUpdatedBy?: CreatedOrUpdatedOrDeletedBy
  deletedBy?: CreatedOrUpdatedOrDeletedBy
  voicemailFile?: { [key: string]: string }
  incomingCallTimeout?: { [key: string]: number }
  token?: string
  loginCount?: number
  sideBarVersion?: string
  pinnedLocations?: string[] | undefined
  userEmailSignature?: { [key: string]: any }
  payRate?: string
  lastKnownLocation?: string
  title?: string
  dirty?: boolean
  twoWaySync?: TwoWaySync | undefined | null
  dialMobileApp?: DialMobileApp
  dialWebApp?: DialWebApp
  saasSettings?:ISaasSettings
  searchShortcut?:boolean
  launchpadCompletedDate?:string
  recentVisited?:{ [key: string]: any }
  increaseLoginCount?: boolean
}

export interface ISaasSettings {
  stripe_connect_initiated: boolean,
  agency_dashboard_visible: boolean
}

const indexwiseWeekdays = {
  '0': 'Sunday',
  '1': 'Monday',
  '2': 'Tuesday',
  '3': 'Wednesday',
  '4': 'Thursday',
  '5': 'Friday',
  '6': 'Saturday',
}

export default class User extends BaseModel {
  public static ROLE_ADMIN = 'admin'
  public static ROLE_USER = 'user'

  public static TYPE_AGENCY = 'agency'
  public static TYPE_ACCOUNT = 'account'

  public previousRole = null

  static requestBodyWhiteList = [
    'firstName',
    'lastName',
    'email',
    'phone',
    'extension',
    'password',
    'profilePhoto',
    'companyId',
    'type',
    'role',
    'locationIds',
    'permissions',
    'userEmailSignature',
    'userCalendar',
    'locationWiseMeetingLocation',
    'locationWiseZoomAdded',
    'locationWiseOpenHours',
    'isPasswordPending',
    'lastLoginTime',
    'source',
    'locationWiseTimezone',
    'dialMobileApp',
    'dialWebApp',
    'twilioPhone',
  ]

  constructor(data?: IUser) {
    super(User.requestBodyWhiteList, data ?? { id: new firestoreUser().ref.id }) // the Id is being used in some places before saving.
    if(!data) {
      this.newRecord = true // this will be set to false by default because of the explicit ID.
    }
  }

  async save(): Promise<IUser> {
    return await new Promise(async (resolve, reject) => {
      try {
        let response: { [key: string]: any }
        if (this.newRecord) {
          let selectedData =
            User.requestBodyWhiteList.length === 0
              ? this.data
              : User.requestBodyWhiteList.reduce((acc, cv) => {
                  acc[cv] = this.data[cv]
                  return acc
                }, {})

          selectedData.locationIds = this.locationIds
          response = await UsersService.create(selectedData as IUser)

          if (response) {
            this.data.id = response.id
            this.newRecord = false
          }
        } else {
          const updatedData = this.updatedData
          if (this.updatedData.locations) {
            updatedData.locationIds = this.locationIds
            delete updatedData.locations
          }
          response = await UsersService.update(this.id, updatedData as IUser)
        }
        this.clearUpdatedData() // clear updatedData on every save so that we don't send in the same after object again.
        this.newRecord = false // once saved, clear new Record to avoid multiple post API calls
        resolve(response as IUser)
      } catch (error) {
        reject(error)
      }
    })
  }

  static async getById(
    userId: string,
  ): Promise<User> {
    const response = await UsersService.read(userId)
    return new User(response)
  }

  get id(): string {
    return this.data.id
  }

  get companyId(): string {
    return this.data.companyId
  }

  set companyId(companyId: string) {
    this.data.companyId = companyId
    this.updatedData.companyId = companyId
  }

  get firstName(): string {
    return this.data.firstName
  }

  set firstName(firstName: string) {
    this.data.firstName = firstName
    this.data.firstNameLowerCase = firstName.toLowerCase()

    this.updatedData.firstName = firstName
  }

  get firstNameLowerCase(): string {
    return this.data.firstNameLowerCase
  }

  get lastName(): string {
    return this.data.lastName
  }

  set lastName(lastName: string) {
    this.data.lastName = lastName
    this.data.lastNameLowerCase = lastName.toLowerCase()
    this.updatedData.lastName = lastName
  }

  get lastNameLowerCase(): string {
    return this.data.lastNameLowerCase
  }

  get name(): string {
    return [this.firstName, this.lastName].filter(val => val).join(' ')
  }

  get fullName(): string {
    return [this.title, this.firstName, this.lastName].filter(d => d).join(' ')
  }

  get shortName(): string {
    return [this.title, this.lastName].filter(d => d).join(' ')
  }

  get email(): string {
    return this.data.email
  }

  set email(email: string) {
    this.data.email = email
    this.updatedData.email = email
  }

  get phone(): string {
    return this.data.phone
  }

  set phone(phone: string) {
    this.data.phone = phone
    this.updatedData.phone = phone
  }

  get twilioPhone(): { [key: string]: string } {
    if (!this.data.twilioPhone) this.data.twilioPhone = {}
    return this.data.twilioPhone
  }

  set twilioPhone(values: { [key: string]: string }) {
    this.data.twilioPhone = values
    this.updatedData.twilioPhone = values
  }

  get userCalendar(): { [key: string]: string } {
    if (!this.data.userCalendar) this.data.userCalendar = {}
    return this.data.userCalendar
  }

  set userCalendar(values: { [key: string]: string }) {
    this.data.userCalendar = values
    this.updatedData.userCalendar = values
  }

  get userEmailSignature(): { [key: string]: {} } {
    if (!this.data.userEmailSignature) this.data.userEmailSignature = {}
    return this.data.userEmailSignature
  }

  set userEmailSignature(values: { [key: string]: any }) {
    this.data.userEmailSignature = values
    this.updatedData.userEmailSignature = values
  }

  get extension(): string {
    return this.data.extension
  }

  set extension(extension: string) {
    this.data.extension = extension
    this.updatedData.extension = extension
  }

  get role(): string {
    return this.data.role
  }

  set role(role: string) {
    this.data.role = role
    this.updatedData.role = role
  }

  get type(): string {
    return this.data.type
  }

  set type(type: string) {
    this.data.type = type
    this.updatedData.type = type
  }

  get title(): string {
    return this.data.title
  }

  set title(title: string) {
    this.data.title = title
    this.updatedData.title = title
  }

  get deleted(): boolean {
    return this.data.deleted
  }

  set deleted(deleted: boolean) {
    this.data.deleted = deleted
  }

  get dirty(): boolean {
    return this.data.dirty
  }

  set dirty(dirty: boolean) {
    this.data.dirty = dirty
  }

  /**
   * Location wise timezone
   * Ex: {'locationId': location specific timezone}
   * */
  get locationWiseTimezone(): { [key: string]: string } {
    return this.data.locationWiseTimezone ?? {}
  }

  set locationWiseTimezone(value: { [key: string]: string }) {
    this.data.locationWiseTimezone = value
    this.updatedData.locationWiseTimezone = value
  }

  /**
   * Location wise meeting location
   * Ex: {'locationId': location specific meeting location}
   * */
  get locationWiseMeetingLocation(): { [key: string]: string } {
    return this.data.locationWiseMeetingLocation ?? {}
  }

  set locationWiseMeetingLocation(value: { [key: string]: string }) {
    this.data.locationWiseMeetingLocation = value
    this.updatedData.locationWiseMeetingLocation = value
  }

  get locationWiseZoomAdded(): { [key: string]: string } {
    return this.data.locationWiseZoomAdded ?? {}
  }

  set locationWiseZoomAdded(value: { [key: string]: string }) {
    this.data.locationWiseZoomAdded = value
    this.updatedData.locationWiseZoomAdded = value
  }

  get isPasswordPending(): boolean {
    return this.data.isPasswordPending
  }

  set isPasswordPending(value: boolean) {
    this.data.isPasswordPending = value
    this.updatedData.isPasswordPending = value
  }

  get scopes(): string[] {
    return this.data.scopes || []
  }

  get scopesAssignedOnly(): string[] {
    return this.data.scopesAssignedOnly || []
  }

  /**
   * Location wise open hours
   * Ex: {'locationId': location open hours}
   * */
  get locationWiseOpenHours(): { [key: string]: { [key: string]: any }[] } {
    return this.data.locationWiseOpenHours ?? {}
  }

  set locationWiseOpenHours(value: {
    [key: string]: { [key: string]: any }[]
  }) {
    this.data.locationWiseOpenHours = value
    this.updatedData.locationWiseOpenHours = value
  }

  get locationWiseDayOfTheWeekwiseAvailability(): {
    [key: string]: { [key: string]: any }
  } {
    const _locationWiseDayOfTheWeekwiseAvailability: {
      [key: string]: { [key: string]: any }
    } = {}
    Object.keys(this.locationWiseOpenHours).forEach(locationId => {
      const _dayOfTheWeekwiseAvailability = {}
      if (this.locationWiseOpenHours[locationId]) {
        this.locationWiseOpenHours[locationId].forEach(x => {
          _dayOfTheWeekwiseAvailability[indexwiseWeekdays[x.days_of_the_week]] =
            x.hours
        })
      }
      _locationWiseDayOfTheWeekwiseAvailability[
        locationId
      ] = _dayOfTheWeekwiseAvailability
    })
    return _locationWiseDayOfTheWeekwiseAvailability
  }

  repeatTheOpenHoursForOtherWeekDays(locationId: string, dayOfWeek: number) {
    const pickedDayOfWeekOpenHours = this.locationWiseOpenHours[
      locationId
    ].find(x => x.days_of_the_week && x.days_of_the_week[0] === dayOfWeek)
    this.locationWiseOpenHours[locationId] = this.locationWiseOpenHours[
      locationId
    ].map(x => {
      return {
        days_of_the_week: x.days_of_the_week,
        hours: cloneDeep(pickedDayOfWeekOpenHours.hours),
      }
    })
  }

  get locations(): { [key: string]: string } {
    return this.data.locations
  }

  set locations(locations: { [key: string]: string }) {
    this.data.locations = locations
    this.updatedData.locations = locations
  }

  get locationIds(): string[] {
    return Object.keys(this.data.locations ?? {})
  }

  get profilePhoto(): string {
    return this.data.profilePhoto
  }

  set profilePhoto(profilePhoto: string) {
    this.data.profilePhoto = profilePhoto
    this.updatedData.profilePhoto = profilePhoto
  }

  get emailProviderId(): string {
    return this.data.emailProviderId
  }

  set emailProviderId(emailProviderId: string) {
    this.data.emailProviderId = emailProviderId
  }

  get emailSignature(): string {
    return this.data.emailSignature
  }

  set emailSignature(emailSignature: string) {
    this.data.emailSignature = emailSignature
    this.updatedData.emailSignature = emailSignature
  }

  set calendar(calendar: string) {
    this.data.calendar = calendar
    this.updatedData.calendar = calendar
  }

  get calendar(): string {
    return this.data.calendar
  }

  get initials(): string {
    let initials = ''
    if (this.firstName) initials += this.firstName.substring(0, 1).toUpperCase()
    if (this.lastName) initials += this.lastName.substring(0, 1).toUpperCase()
    return initials
  }

  get password(): string {
    return this.data.password
  }

  // password won't be stored in user doc. This is just for our backend API to update the private user and hash
  set password(password: string) {
    this.data.password = password
    this.updatedData.password = password
  }

  get permissions(): Permissions {
    if (!this.data.permissions)
      this.data.permissions = {
        campaignsEnabled: true,
        campaignsReadOnly: false,
        workflowsEnabled: true,
        workflowsReadOnly: false,
        contactsEnabled: true,
        triggersEnabled: true,
        opportunitiesEnabled: true,
        settingsEnabled: true,
        tagsEnabled: true,
        leadValueEnabled: true,
        dashboardStatsEnabled: true,
        bulkRequestsEnabled: true,
        appointmentsEnabled: true,
        reviewsEnabled: true,
        onlineListingsEnabled: true,
        phoneCallEnabled: true,
        conversationsEnabled: true,
        marketingEnabled: true,
        botService: false,
        websitesEnabled: true,
        membershipEnabled: true,
        invoiceEnabled: true,
        bloggingEnabled: true,
        communitiesEnabled: true,
        certificatesEnabled:true,
        mediaStorageEnabled:true,
        quizzesEnabled:true,
        qrCodesEnabled: true
      }
    if (this.data.permissions.funnelsEnabled === undefined) {
      this.data.permissions.funnelsEnabled = true
    }

    if (this.data.permissions.websitesEnabled === undefined) {
      this.data.permissions.websitesEnabled =
        this.type === User.TYPE_AGENCY && this.role === User.ROLE_ADMIN
    }

    if (this.data.permissions.membershipEnabled === undefined) {
      this.data.permissions.membershipEnabled =
        this.type === User.TYPE_AGENCY && this.role === User.ROLE_ADMIN
    }

    if (this.data.permissions.attributionsReportingEnabled === undefined) {
      this.data.permissions.attributionsReportingEnabled =
        this.type === User.TYPE_AGENCY && this.role === User.ROLE_ADMIN
    }

    return this.data.permissions
  }

  set permissions(_permissions: Permissions | OldPermission) {
    const permissions = User.parsePermissions(_permissions) // in case we set using underscore
    this.data.permissions = permissions
    this.updatedData.permissions = permissions
  }

  set createdBy(value: CreatedOrUpdatedOrDeletedBy) {
    this.data.createdBy = value
  }

  get lastUpdatedBy(): CreatedOrUpdatedOrDeletedBy {
    return this.data.lastUpdatedBy
  }
  set lastUpdatedBy(value: CreatedOrUpdatedOrDeletedBy) {
    this.data.lastUpdatedBy = value
  }

  get platformLanguage(): string {
    return this.data.platformLanguage;
  }

  set platformLanguage(data: string) {
    this.data.platformLanguage = data;
    this.updatedData.platformLanguage = data;
  }

  public async setCreatedByAndOrLastUpdatedBy(
    isNewUser: boolean,
    source: UserSource,
    channel: UserSourceChannel,
    timestamp: moment.Moment = moment(),
    userId?: string
  ) {
    if (isNewUser) {
      this.createdBy = {
        source,
        channel,
        timestamp: firebase.firestore.Timestamp.fromMillis(timestamp.valueOf()),
      }
    }
    this.lastUpdatedBy = {
      source,
      channel,
      timestamp: firebase.firestore.Timestamp.fromMillis(timestamp.valueOf()),
    }
    if (source) {
      this.updatedData.source = source // other details are update on the marketplace backend
      this.data.source = source
    }
  }

  get profileColor(): string {
    let str = this.fullName
    if (this.phone) str += this.phone
    if (this.email) str += this.email
    let hash = 0
    if (str.length == 0) return '#afb8bc'
    for (let i = 0; i < str.length; i++) {
      hash = str.charCodeAt(i) + ((hash << 5) - hash)
      hash = hash & hash
    }

    const shortened = Math.abs(hash) % 360
    return 'hsl(' + shortened + ',35%, 60%)'
  }

  get voicemailFile(): { [key: string]: string } {
    if (!this.data.voicemailFile) this.data.voicemailFile = {}
    return this.data.voicemailFile
  }

  set voicemailFile(voicemailFile: { [key: string]: string }) {
    this.data.voicemailFile = voicemailFile
    this.updatedData.voicemailFile = voicemailFile
  }

  get token(): string {
    return this.data['token']
  }

  set token(token: string) {
    this.data['token'] = token
  }

  get loginCount(): number {
    return this.data['loginCount'] ? this.data['loginCount'] : 0
  }

  set loginCount(count: number) {
    this.data['loginCount'] = count
  }
  get dialMobileApp(): DialMobileApp {
    return this.data?.dialMobileApp || {}
  }
  set dialMobileApp(dialMobileApp: DialMobileApp) {
    this.data.dialMobileApp = dialMobileApp
    this.updatedData.dialMobileApp = dialMobileApp
  }

  get dialWebApp(): DialWebApp {
    return this.data?.dialWebApp || {}
  }
  set dialWebApp(dialWebApp: DialWebApp) {
    this.data.dialWebApp = dialWebApp
    this.updatedData.dialWebApp = dialWebApp
  }

  get lastLoginTime(): moment.Moment | undefined {
    return this.data['lastLoginTime']
      ? moment(this.data['lastLoginTime'].toMillis())
      : undefined
  }

  set lastLoginTime(time: moment.Moment | undefined) {
    this.data['lastLoginTime'] = time.format()
  }

  get incomingCallTimeout(): { [key: string]: number } {
    if (!this.data.incomingCallTimeout) this.data.incomingCallTimeout = {}
    return this.data.incomingCallTimeout
  }

  set incomingCallTimeout(incomingCallTimeout: { [key: string]: number }) {
    this.data.incomingCallTimeout = incomingCallTimeout
    this.updatedData.incomingCallTimeout = incomingCallTimeout
  }

  set saasSettings(saasSettings: ISaasSettings) {
    this.data['saasSettings'] = saasSettings
  }

  get saasSettings(): ISaasSettings {
    return this.data['saasSettings']
  }

  get twoWaySync(): TwoWaySync {
    return this.data['two_way_sync'];
  }
  get pinnedLocations(): string[] {
    return this.data['pinnedLocations'];
  }
  set pinnedLocations(data:string[]) {
    this.data['pinnedLocations'] = data
  }

  set twoWaySync(twoWaySync: TwoWaySync) {
    this.data['two_way_sync'] = twoWaySync
  }

  get invitedForGmailTwoWaySync(): boolean {
    return this.data.invited_for_gmail_two_way_sync
  }

  set invitedForGmailTwoWaySync(invitedForGmailTwoWaySync: boolean){
    this.data.invited_for_gmail_two_way_sync = invitedForGmailTwoWaySync
  }

  get launchpadCompletedDate(): moment.Moment | undefined {
    return this.data['launchpad_completed_date']
      ? moment(this.data['launchpad_completed_date'].toMillis())
      : undefined
  }

  set launchpadCompletedDate(date: moment.Moment | undefined) {
    if (date) {
      this.data[
        'launchpad_completed_date'
      ] = firebase.firestore.Timestamp.fromMillis(date.valueOf())
    } else {
      this.data['launchpad_completed_date'] = date
    }
  }

  /**
   * Parses new User params from firestore model
   * @param user (firestore user model)
   * @returns IUser which is used in new API
   */
  static prepareUserParams(user: firestoreUser): IUser {
    const result = {
      firstName: user.firstName,
      lastName: user.lastName,
      email: user.email,
      phone: user.phone,
      extension: user.extension,
      role: user.role,
      type: user.type,
      companyId: user.companyId,
      locations: user.locations,
      twilioPhone: user.twilioPhone,
      lastLoginTime: user.lastLoginTime,
      id: user.id,
      userCalendar: user.userCalendar,
      locationWiseTimezone: user.locationWiseTimezone,
      locationWiseMeetingLocation: user.locationWiseMeetingLocation,
      locationWiseZoomAdded: user.locationWiseZoomAdded,
      locationWiseOpenHours: user.locationWiseOpenHours,
      isPasswordPending: user.isPasswordPending,
      profilePhoto: user.profilePhoto,
      emailProviderId: user.emailProviderId,
      emailSignature: user.emailSignature,
      calendar: user.calendar,
      postedForSaas: user.postedForSaas,
      permissions: this.parsePermissions(user.permissions),
      createdBy: user.createdBy,
      lastUpdatedBy: user.lastUpdatedBy,
      deletedBy: user.deletedBy,
      voicemailFile: user.voicemailFile,
      incomingCallTimeout: user.incomingCallTimeout,
      token: user.token,
      loginCount: user.loginCount,
      sideBarVersion: user.sideBarVersion,
      pinnedLocations: user.pinnedLocations,
      userEmailSignature: user.userEmailSignature,
      payRate: user.payRate,
      title: user.title,
      dirty: user.dirty,
      dialMobileApp:user.dialMobileApp,
      dialWebApp: user.dialWebApp,
      platformLanguage: user.platformLanguage
    }

    return result
  }

  static parsePermissions(
    _permissions: OldPermission | Permissions
  ): Permissions {
    const permissions = {}

    Object.keys(_permissions).forEach(key => {
      permissions[camelCase(key)] = _permissions[key]
    })

    return permissions
  }

  /**
   * Returns new API User from a given firestore user model
   * @param user
   * @returns
   */
  static getUserFromFirestoreModel(user: firestoreUser) {
    return new User(this.prepareUserParams(user))
  }
}
